import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { X, Download } from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import apiService from '../../services/api';
import { hasPermission, PERMISSIONS } from '../../utils/authRoles';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  documento: any | null;
}

const DocumentPreviewModal: React.FC<Props> = ({ isOpen, onClose, documento }) => {
  const [loading, setLoading] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [mime, setMime] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const { user: authUser } = useAuthStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const canDownload = hasPermission(authUser, PERMISSIONS.DOCUMENTS_RESTRICTED_DOWNLOAD);

  // 'certificado' se excluye a propósito: los cursos/certificados deben ser visibles para todos los usuarios.
  const RESTRICTED_TIPO_KEYWORDS = ['contrato', 'f30', 'cotizacion', 'cotizaciones', 'liquidacion', 'sueldo'];
  // Cursos/certificaciones: nunca restringidos (visibles para todos los usuarios)
  const COURSE_TIPOS = new Set(['curso', 'certificacion', 'curso/certificacion', 'certificado_curso', 'diploma', 'certificado_seguridad', 'certificado_vencimiento']);
  const isCourseDoc = (doc: any) => COURSE_TIPOS.has(String(doc?.tipo_documento || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim());
  const normalizeForMatch = (s: string) => {
    if (!s) return '';
    return String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };
  const isRestrictedDoc = (doc: any) => {
    if (isCourseDoc(doc)) return false;
    const tipo = normalizeForMatch(doc?.tipo_documento || '');
    const nombre = normalizeForMatch(doc?.nombre_documento || doc?.nombre_original || doc?.nombre_archivo || '');
    return RESTRICTED_TIPO_KEYWORDS.some(kw => tipo.includes(kw) || nombre.includes(kw));
  };

  useEffect(() => {
    let url: string | null = null;
      const load = async () => {
      if (!isOpen || !documento?.id) return;
      setLoading(true);
        setAccessDenied(false);
        // Si el documento es restringido y el usuario no tiene rol privilegiado, impedir la petición
        if (isRestrictedDoc(documento) && !hasPermission(authUser, PERMISSIONS.DOCUMENTS_RESTRICTED_VIEW)) {
          console.warn('Acceso denegado a vista previa para documento restringido y usuario sin permiso', { documentoId: documento.id, role: authUser?.rol });
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        try {
          setErrorMessage(null);
          const res = await apiService.visualizarDocumento(documento.id);
          const blob = res.blob;
          const m = blob.type || 'application/octet-stream';
          url = URL.createObjectURL(blob);
          setObjectUrl(url);
          setMime(m);
        } catch (e: any) {
          console.error('Error al obtener documento para vista previa', e);
          if (e?.response?.status === 403) {
            setAccessDenied(true);
          } else {
            const msg = e?.response?.data?.mensaje || e?.message || String(e);
            setErrorMessage(msg);
          }
        } finally {
          setLoading(false);
        }
    };

    load();

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
      setObjectUrl(null);
      setMime(null);
    };
  }, [isOpen, documento]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-60 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold">Vista previa: {documento?.nombre_documento || documento?.nombre_original || 'Documento'}</h3>
            <span className="text-xs text-gray-500">{documento?.tipo_documento}</span>
          </div>
          <div className="flex items-center gap-2">
            {objectUrl && (canDownload || isCourseDoc(documento)) && (
              <a href={objectUrl} download={documento?.nombre_original || documento?.nombre_archivo || documento?.nombre_documento} className="text-sm text-gray-700 px-3 py-1 rounded hover:bg-gray-100 flex items-center gap-2">
                <Download className="w-4 h-4" /> Descargar
              </a>
            )}
            <button onClick={onClose} className="text-gray-600 p-2 rounded hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 h-[75vh] overflow-auto flex items-center justify-center">
          {loading ? (
            <LoadingSpinner />
            ) : objectUrl ? (
            (mime || '').includes('pdf') ? (
              <iframe src={objectUrl} title="preview" className="w-full h-full border" />
            ) : mime?.startsWith('image/') ? (
              // image
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={objectUrl} alt="Imagen documento" className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="text-center">
                <p className="text-sm text-gray-700 mb-3">Tipo de archivo no compatible para vista previa: {mime}</p>
                <a href={objectUrl} download={documento?.nombre_original || documento?.nombre_archivo || documento?.nombre_documento} className="text-sm text-blue-600">Descargar archivo</a>
              </div>
            )
          ) : accessDenied ? (
            <div className="text-sm text-gray-500">Acceso denegado. No tienes permisos para ver este documento.</div>
          ) : errorMessage ? (
            <div className="text-sm text-gray-500">Error: {errorMessage}</div>
          ) : (
            <div className="text-sm text-gray-500">No se pudo obtener la vista previa.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;

